import React, { useState } from 'react';
import axios from 'axios';
import { FaStar, FaRegStar } from "react-icons/fa";
import CommonModal from './CommonModal';
import { apiBaseUrl } from '../../config';
import { toast } from 'react-toastify'; 

const ReviewModal = ({ isOpen, onClose, productId, onSubmitSuccess }) => {
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleRatingChange = (newRating) => {
    setRating(newRating);
    if (error) setError('');
  };

  const renderStarSelection = () => {
    return [1, 2, 3, 4, 5].map((starValue) => (
      <button
        type="button"
        key={starValue}
        className={`star-button ${starValue <= rating ? 'selected' : ''}`}
        onClick={() => handleRatingChange(starValue)}
      >
        {starValue <= rating ? <FaStar /> : <FaRegStar />}
      </button>
    ));
  };

  const clearForm = () => {
    setRating(0);
    setTitle('');
    setDescription('');
    setError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (rating === 0) {
      setError('Please select a rating.');
      return;
    }
    if (!description.trim()) {
      setError('Please enter a description for your review.');
      return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication required. Please log in again.');
        setIsSubmitting(false);
        return;
      }

      const reviewData = {
        productId,
        rating,
        title: title.trim(),
        description: description.trim(),
      };

      const response = await axios.post(
        `${apiBaseUrl}customer/add-review`,
        reviewData,
        {
          headers: {
            'Content-Type': 'application/json',
            token: token
          }
        }
      );

      if (response.status === 201 && response.data.success) {
        toast.success('Review submitted successfully!');
        clearForm();
        onSubmitSuccess();
        onClose();
      } else {
        throw new Error(response.data.message || 'Failed to submit review.');
      }

    } catch (err) {
      console.error("Review Submission Error:", err);
      
      let displayErrorMessage = 'An error occurred while submitting your review.';

      if (err.response) {
        if (err.response.status === 409) {
          displayErrorMessage = err.response.data?.message || 'You have already reviewed this product.';
        } else {
          displayErrorMessage = err.response.data?.error || err.response.data?.message || err.message;
        }
      } else {
        displayErrorMessage = err.message;
      }

      setError(displayErrorMessage); 
      toast.error(displayErrorMessage); 
    } finally {
      setIsSubmitting(false);
    }
  };

  // Define the form content as a JSX element
  const reviewFormContent = (
    <form onSubmit={handleSubmit} className="review-form">
      {error && <p className="error-message">{error}</p>}

      <div className="form-group">
        <label className="form-label">Your Rating*:</label>
        <div className="star-rating-input">
          {renderStarSelection()}
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="reviewTitle" className="form-label">Review Title (Optional):</label>
        <input
          type="text"
          id="reviewTitle"
          className="form-control"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Summarize your experience"
          maxLength={100}
        />
      </div>

      <div className="form-group">
        <label htmlFor="reviewDescription" className="form-label">Your Review*:</label>
        <textarea
          id="reviewDescription"
          className="form-control"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Tell us more about your experience..."
          rows={4}
          required
          maxLength={1000}
        />
      </div>

      <div className="form-actions">
        <button
          type="submit"
          className="submit-button"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Submitting...' : 'Submit Review'}
        </button>
      </div>
    </form>
  );

  
  return isOpen ? (
    <CommonModal
      show={isOpen}
      handleClose={onClose}
      title="Write a Review"
      content={reviewFormContent}
    />
  ) : null;
};

export default ReviewModal;